import { Permissions } from "../features/navigation/Permissions";
import {
    advanceSubscription,
    AvataxRole,
    avaTaxSubscription,
    avataxTiers,
    customerSubscription,
    ecmRETRole,
    EcmRole,
    ecmSubscription,
    lowerSubscription,
    Subscription,
    userRoleAdminSubscription,
    vendorAvaTaxAPSubscription,
    vendorExemptions,
    vendorPremiumOnlySubscription,
    vendorSubscription,
} from "../features/navigation/Subscription";
import { Environments, lowerEnvironments, upperEnvironments } from "../shared/constants";
import { SessionChecks, type FeatureFlags } from "./types";

const featureFlag: FeatureFlags = {
    zoomInWidget: {
        id17: {
            name: "zoomInWidgetId17",
            description: "zoomIn widget with id 17",
            subscription: [Subscription.ECMPro],
        },
        id18: {
            name: "zoomInWidgetId18",
            description: "zoomIn widget with id 18",
            subscription: [Subscription.ECMPremium],
        },
        id23: {
            name: "zoomInWidgetId23",
            description: "zoomIn widget with id 23",
            subscription: [Subscription.AvaTaxExemptions],
        },
    },
    topNav: {
        activateSettingsTab: {
            name: "activateSettingsTab",
            description: "Select settings as active tab on AuiSupernav",
            subscription: [Subscription.AVATAXAP],
        },
        premiumCategory: {
            name: "premiumCategory",
            description: "Display premium in top nav category",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
        },
        proCategory: {
            name: "proCategory",
            description: "Display pro in top nav category",
            subscription: [Subscription.ECMPro, Subscription.VEPRO],
        },
        essentialsCategory: {
            name: "essentialsCategory",
            description: "Display essentials in top nav category",
            subscription: [Subscription.ECMEssentials],
        },
    },
    resyncCertificate: {
        resync: {
            name: "resync",
            description: "resync certificate",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            checkAdmin: true,
            setting: "hasExternalApi",
            permissions: [Permissions.certificate.resync.index],
        },
    },
    validatedCertificate: {
        revalidate: {
            name: "revalidateCertificate",
            description: "Revalidate certificate functionality",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificate.create],
        },
        delete: {
            name: "delete",
            description: "Delete a certificate",
            subscription: ["All"],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            checkAdmin: true,
            permissions: [Permissions.certificate.delete],
        },
        validate: {
            name: "validate",
            description: "Validate a certificate",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.RETManager],
            checkAdmin: true,
            permissions: [Permissions.certificate.update],
        },
        vendorSave: {
            name: "vendorSave",
            description: "Validate a certificate",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            checkAdmin: true,
        },
        saveAsValid: {
            name: "saveAsValid",
            description: "Validate a certificate(only save as valid, for lower subscriptions)",
            subscription: lowerSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            checkAdmin: true,
            permissions: [Permissions.certificate.update],
        },
        upload: {
            name: "upload",
            description: "File upload",
            subscription: [...ecmSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificate.file.update],
        },
        attachments: {
            name: "attachments",
            description: "Supplemental files of certificate",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificate.file.update],
        },
        comments: {
            name: "comments",
            description: "Manage comments on certificate",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificate.update],
        },
        notification: {
            name: "Notification",
            description:
                "Send Notification: a flag is sent to post certificate API to send notification",
            subscription: advanceSubscription,
        },
        pauseResume: {
            name: "pauseResume",
            description: "pause and resume button",
            subscription: ["All"],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificate.pauseResume.update],
        },
        customFields: {
            name: "customFields",
            description: "view certificate custom fields",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.MSUser],
            permissions: [Permissions.certificate.read],
        },
        showInvalidAsOption: {
            name: "showInvalidAsOption",
            description: "certificate custom fields",
            subscription: advanceSubscription,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
        },
        certificateLabels: {
            name: "certificateLabels",
            description: "view certificate labels",
            subscription: [
                Subscription.ECMPremium,
                Subscription.ECMPro,
                Subscription.VEPREMIUM,
                Subscription.VEPRO,
            ],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            permissions: [Permissions.certificate.read],
        },
        customerLabels: {
            name: "customerLabels",
            description: "view customer labels",
            subscription: [
                Subscription.ECMPremium,
                Subscription.ECMPro,
                Subscription.VEPREMIUM,
                Subscription.VEPRO,
            ],
            permissions: [Permissions.certificate.read],
        },
        view: {
            name: "view",
            description: "View certificate file",
            subscription: [...ecmSubscription, Subscription.VEPRO, Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.certificate.read],
        },
        magnifierToggle: {
            name: "magnifierToggle",
            description:
                "Show magnifier toggle on validated certificate detail (hidden for AvaTaxExemptions)",
            subscription: [
                Subscription.ECMEssentials,
                ...advanceSubscription,
                ...vendorSubscription,
            ],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.certificate.read],
        },
        locationDropdown: {
            name: "locationDropdown",
            description: "View certificate location dropdown",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.RETManager,
                EcmRole.RETAssociate,
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
            ],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            setting: "enableRetail",
        },
    },
    reviewCertificate: {
        removeUnrelatedRegions: {
            name: "removeUnrelatedRegions",
            description: "allows the user to remove unrelated regions",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMPreValidator,
                EcmRole.MSUser,
            ],
            permissions: [Permissions.pendingCertificate.view],
        },
        add: {
            name: "add",
            description: "Add a certificate",
            subscription: [...lowerSubscription, ...advanceSubscription],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [
                Permissions.pendingCertificate.write,
                Permissions.certificate.create,
                Permissions.certificate.upload,
            ],
            requireAnyPermission: true,
        },
        update: {
            name: "update",
            description: "Update an existing pending certificate",
            subscription: [...lowerSubscription, ...advanceSubscription],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write, Permissions.certificate.upload],
            requireAnyPermission: true,
        },
        productCategories: {
            name: "productCategories",
            description: "Limit an exemption to certain product categories while reviewing",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.settings.exemptProductCategories.read],
            setting: "exemptionCategory",
        },
        // Makes the product category mandatory on review (CERTCAP-67016).
        // Product asked for the field to stay optional for now, so this is off
        // everywhere: `env: []` matches no environment, which is how
        // useIsAuthorized reads "hidden". To switch the requirement back on,
        // give it the environments to enable, e.g.
        // `env: [...lowerEnvironments, ...upperEnvironments]` - nothing else
        // needs to change. Display of the field is gated separately by
        // `productCategories` above and stays on.
        productCategoriesRequired: {
            name: "productCategoriesRequired",
            description: "Require a product category before a certificate can be validated",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.settings.exemptProductCategories.read],
            setting: "exemptionCategory",
            env: [],
        },
        completeOrPending: {
            name: "completeOrPending",
            description: "create a complete(valid or invalid) or pending certificate",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
            ],
            permissions: [Permissions.pendingCertificate.complete],
            checkAdmin: true,
        },
        complete: {
            name: "complete",
            description: "create a complete certificate",
            subscription: lowerSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.complete],
        },
        delete: {
            name: "delete",
            description: "Delete a certificate",
            subscription: ["All"],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.delete],
        },
        upload: {
            name: "upload",
            description: "File upload",
            subscription: [...ecmSubscription, Subscription.VEPRO, Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write, Permissions.certificate.upload],
            requireAnyPermission: true,
        },
        attachments: {
            name: "attachments",
            description: "Supplemental files of certificate",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.RETManager, EcmRole.MSUser],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write],
        },
        comments: {
            name: "comments",
            description: "Comments on certificate",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMViewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write],
        },
        split: {
            name: "split",
            description: "File split",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write],
        },
        assignRelease: {
            name: "assignRelease",
            description: "assign and release button",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.complete],
        },
        escalate: {
            name: "escalate",
            description: "escalate certificates",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.RETManager,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.complete],
        },
        reviewNextCertificate: {
            name: "reviewNextCertificate",
            description: "review next certificate feature",
            subscription: [Subscription.ECMPro, Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.complete],
        },
        emailNotifications: {
            name: "emailNotifications",
            description: "email notifications on validation",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            permissions: [Permissions.certificate.read],
        },
        locationDropdown: {
            name: "locationDropdown",
            description: "View review certificate location dropdown",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.RETManager,
                EcmRole.RETAssociate,
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
            ],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            setting: "enableRetail",
        },
        duplicateCertificateCheck: {
            name: "duplicateCertificateCheck",
            description: "Show matching certificates before a single-region certificate is saved",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.complete],
        },
        multiRegionSaveChanges: {
            name: "multiRegionSaveChanges",
            description: "Enable Save Changes for multi-jurisdictional (multi-region) certificates",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.complete],
            env: [Environments.DEV],
        },
    },
    validatedSearchPage: {
        viewValidatedCertificates: {
            name: "viewValidateCertificates",
            description: "view validated certificates search page",
            subscription: [...lowerSubscription, ...advanceSubscription],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.certificate.read],
        },
        manageEcmReports: {
            name: "manageEcmReports",
            description: "CRUD operation on save searches",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMManager,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.savedSearch.index.create],
        },
        exportCertificateImages: {
            name: "exportCertificateImages",
            description: "Download Certificate Images in Bulk",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.export.read],
        },
        export: {
            name: "export",
            description: "Export search filter result",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            permissions: [Permissions.export.read],
        },
        bulkDelete: {
            name: "bulkDelete",
            description: "Delete certificates in bulk",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.certificate.bulkDelete.delete],
        },
        bulkEdit: {
            name: "bulkEdit",
            description: "Bulk update the certificates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificate.bulkEdit.labels.update],
        },
        importCertificates: {
            name: "importCertificates",
            description: "Import certificates in bulk",
            subscription: [...advanceSubscription, ...lowerSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            checkAdmin: true,
            permissions: [Permissions.certificate.bulkUpload.create],
        },
        ellipsisMenu: {
            name: "ellipsisMenu",
            description: "view certificate ellipsis menu options",
            subscription: [...lowerSubscription, ...advanceSubscription],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            checkAdmin: true,
            permissions: [Permissions.certificate.read],
            requireAnyPermission: true,
        },
    },
    reviewSearchPage: {
        viewReviewSearchPage: {
            name: "viewReviewSearchPage",
            description: "view review search page",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.RETManager,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.pendingCertificate.view],
        },
        bulkDelete: {
            name: "bulkDelete",
            description: "Delete certificates in bulk",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMCertificateReviewer],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.delete],
        },
        changeBucket: {
            name: "changeBucket",
            description: "Change bucket",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMCertificateReviewer],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write],
            env: [...lowerEnvironments, ...upperEnvironments],
        },
        changePriority: {
            name: "changePriority",
            description: "Assign priority",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMCertificateReviewer],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write],
        },
        zipUploadCertificate: {
            name: "zipUploadCertificate",
            description: "Zip upload certificates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.pendingCertificate.write],
        },
        manageEcmReports: {
            name: "manageEcmReports",
            description: "CRUD operation on save searches",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMManager,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.savedSearch.index.update],
            checkAdmin: true,
        },
    },
    customers: {
        lastTransactionDate: {
            name: "lastTransactionDate",
            description: "Last transaction date column/field (Customer)",
            subscription: [Subscription.ECMPro, Subscription.ECMPremium],
            setting: "showLastTransactionDate",
        },
        viewCustomer: {
            name: "manageCustomer",
            description: "Create and edit a customer",
            subscription: [...lowerSubscription, ...advanceSubscription],
            permissions: [Permissions.customer.read],
        },
        manageCustomer: {
            name: "manageCustomer",
            description: "Create and edit a customer",
            subscription: [...lowerSubscription, ...advanceSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.customer.write],
        },
        deleteCustomer: {
            name: "deleteCustomer",
            description: "Delete a customer",
            subscription: [...lowerSubscription, ...advanceSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.customer.delete],
        },
        export: {
            name: "manageCustomerExport",
            description: "Export search filter result",
            subscription: customerSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.export.read],
        },
        customFields: {
            name: "customFields",
            description: "customer custom fields",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.customer.customFields.read],
        },
        customerLabels: {
            name: "customerLabels",
            description: "customer labels",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.customer.label.read],
        },
        showCustomFields: {
            name: "showCustomFields",
            description: "show customer custom fields",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.customer.customFields.read],
        },
        showCustomerLabels: {
            name: "showCustomerLabels",
            description: "show customer labels",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.customer.label.read],
        },
        parentTag: {
            name: "parent Tag",
            description: "Parent customer tag visibility",
            subscription: advanceSubscription,
        },
        relatedCustomer: {
            name: "relatedCustomer",
            description: "Bill-to and ship-to relationships",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.customer.write],
        },
        viewRelatedCustomer: {
            name: "viewRelatedCustomer",
            description: "View Bill-to and ship-to relationships",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
                EcmRole.CEMCertificateReviewer,
            ],
            permissions: [Permissions.customer.read],
        },
        duplicateCustomer: {
            name: "duplicatecustomers",
            description: "Duplicate customer tab visibility",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.customer.read],
        },
        updateDuplicateCustomer: {
            name: "updateDuplicateCustomer",
            description: "Duplicate customer tab visibility",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.customer.write],
        },
        viewCommentsAndAttachments: {
            name: "viewCommentsAndAttachments",
            description: "View comments and attachments of customer",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
                EcmRole.CEMViewer,
                EcmRole.CEMCertificateReviewer,
            ],
            permissions: [Permissions.customer.commentsAndAttachments.read],
        },
        updateCommentsAndAttachments: {
            name: "updateCommentsAndAttachments",
            description: "Update comments and attachments of customer",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.customer.commentsAndAttachments.update],
        },
        saveSearches: {
            name: "saveSearches",
            description: "Save filtered result",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.savedSearch.index.create],
            checkAdmin: true,
        },
        bulkDelete: {
            name: "bulkDelete",
            description: "Delete customers in bulk",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            checkAdmin: true,
            permissions: [Permissions.customer.bulk],
        },
        import: {
            name: "importCustomers",
            description: "Show import customers on customer search",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.customer.bulk],
        },
        showAllImportTemplate: {
            name: "showAllImportTemplate",
            description: "show all templates on Import customers",
            subscription: [
                ...ecmSubscription,
                ...vendorExemptions,
                ...vendorAvaTaxAPSubscription,
                ...avataxTiers,
            ],
            permissions: [Permissions.customer.bulk],
        },
        viewCertificates: {
            name: "viewCertificates",
            description: "view complete or pending certificates",
            subscription: avaTaxSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.certificate.read],
        },
        viewPendingCertificates: {
            name: "viewPendingCertificates",
            description: "view pending certificates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.RETManager],
        },
    },
    companyGroups: {
        companyGroups: {
            name: "companyGroups",
            description: "Company group filter visibility",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
                EcmRole.MSUser,
            ],
            permissions: [Permissions.settings.companyGroups.read],
        },
    },
    settings: {
        msAutomation: {
            name: "msAutomation",
            description: "Managed Service auto-validation dashboard (/managed-services)",
            // Gated on internal (TSA) users only; the managed_service_user role is no longer required.
            subscription: ["All"],
            internalUser: true,
        },
        requireCertificateImage: {
            name: "requireCertificateImage",
            description: "This Setting forces user to upload image while creating certificate",
            subscription: advanceSubscription,
        },
        general: {
            name: "general",
            description: "Description of your products or services",
            subscription: [...advanceSubscription, Subscription.VEPREMIUM, Subscription.VEPRO],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalUpdate],
        },
        removeExtraRegions: {
            name: "removeExtraRegions",
            description: "Setting for remove extra regions option selection",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalRead],
        },
        duplicateCertificateCheck: {
            name: "duplicateCertificateCheck",
            description:
                "Setting that turns on the matching certificate check for single-region certificates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalUpdate],
        },
        automations: {
            name: "automations",
            description: "Request certificates automatically",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalRead],
        },
        // // Automations and requestingCertficateExemptionMatrix can be replaced by certificateRequestOptions
        // certificateRequestOptions: {
        //     name: "certificateRequestOptions",
        //     description: "Certificate request options",
        //     subscription: advanceSubscription,
        //     ecmRole: [EcmRole.CEMAdmin],
        //     checkAdmin: true,
        //     permissions: [
        //         Permissions.settings.certificateRequestOption.read,
        //         Permissions.settings.certificateRequestOption.update,
        //     ],
        // },
        requestingCertficateExemptionMatrix: {
            name: "requestingCertficateExemptionMatrix",
            description: "Certificate creation tool",
            subscription: [...advanceSubscription],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.certificateExemptionForm.incoming.read],
        },
        // todo-nk
        sendingCertficateExemptionMatrix: {
            name: "sendingCertficateExemptionMatrix",
            description: "send certificate",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.certificateExemptionForm.outgoing.read],
        },
        requestingCertExemptionMatrixUpdate: {
            name: "requestingCertExemptionMatrixUpdate",
            description: "Update the exemption matrix of reqesting certificates",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.certificateExemptionForm.incoming.update],
        },
        enableAutoSaveForMatrix: {
            name: "enableAutoSaveForMatrix",
            description: "Enable auto-save for certificate exemption matrix",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.certificateExemptionForm.incoming.update],
            env: lowerEnvironments,
        },
        // todo-nk
        sendingCertExemptionMatrixUpdate: {
            name: "sendingCertExemptionMatrixUpdate",
            description: "Update the exemption matrix of sending certificates",
            subscription: [Subscription.VEPREMIUM, Subscription.VEPRO],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
        },
        vendorOptions: {
            name: "vendorOptions",
            description: "Update the vendor options",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.accountSignature.index.update],
        },
        viewExemptionReason: {
            name: "viewExemptionReason",
            description: "View all exemption reasons",
            subscription: [
                Subscription.ECMPremium,
                Subscription.ECMPro,
                Subscription.VEPREMIUM,
                Subscription.VEPRO,
            ],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.exemptionReasons.index.read],
            checkAdmin: true,
        },
        addExemptionReason: {
            name: "addExemptionReason",
            description: "Add an exemption reason",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.exemptionReasons.index.create],
            checkAdmin: true,
        },
        editExemptionReason: {
            name: "editExemptionReason",
            description: "Edit an exemption reason",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.exemptionReasons.index.update],
            checkAdmin: true,
        },
        deleteExemptionReason: {
            name: "deleteExemptionReason",
            description: "Delete an exemption reason",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.exemptionReasons.index.delete],
            checkAdmin: true,
        },
        disableExemptionReason: {
            name: "disableExemptionReason",
            description: "Disable an exemption reason",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.exemptionReasons.index.delete],
            checkAdmin: true,
        },
        viewExemptionRegion: {
            name: "viewExemptionRegion",
            description: "View all exemption regions",
            subscription: [
                Subscription.ECMPremium,
                Subscription.ECMPro,
                Subscription.VEPREMIUM,
                Subscription.VEPRO,
            ],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.region.read],
        },
        addExemptionRegion: {
            name: "addExemptionRegion",
            description: "Add an exemption region",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.region.create],
        },
        editExemptionRegion: {
            name: "editExemptionRegion",
            description: "Edit an exemption region",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.region.update],
        },
        deleteExemptionRegion: {
            name: "deleteExemptionRegion",
            description: "Delete an exemption region",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.region.delete],
        },
        viewInvalidReason: {
            name: "viewInvalidReason",
            description: "View all invalid reasons",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.invalidReasons.index.read],
            checkAdmin: true,
        },
        addInvalidReason: {
            name: "addInvalidReason",
            description: "Add an invalid reason",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.invalidReasons.index.create],
            checkAdmin: true,
        },
        editInvalidReason: {
            name: "editInvalidReason",
            description: "Edit an invalid reason",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.invalidReasons.index.update],
            checkAdmin: true,
        },
        deleteInvalidReason: {
            name: "deleteInvalidReason",
            description: "Delete an invalid reason",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.invalidReasons.index.delete],
            checkAdmin: true,
        },

        viewCustomField: {
            name: "viewCustomField",
            description: "View all customers & certificates custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCustomFields.read],
        },
        viewCustomersCustomField: {
            name: "viewcustomersCustomField",
            description: "View all customers custom field",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCustomFields.read],
        },
        addCustomersCustomField: {
            name: "addCustomersCustomField",
            description: "Add a customers custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCustomFields.create],
        },
        editCustomersCustomField: {
            name: "editCustomersCustomField",
            description: "Edit a customers custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCustomFields.update],
        },
        deleteCustomersCustomField: {
            name: "deleteCustomersCustomField",
            description: "Delete a customers custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCustomFields.delete],
        },
        viewVendorsCustomField: {
            name: "viewVendorCustomField",
            description: "View all vendor custom field",
            subscription: [Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCustomFields.read],
        },
        addVendorsCustomField: {
            name: "addVendorCustomField",
            description: "Add a vendor custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCustomFields.create],
        },
        editVendorsCustomField: {
            name: "editVendorCustomField",
            description: "Edit a vendor custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCustomFields.update],
        },
        deleteVendorsCustomField: {
            name: "deleteVendorCustomField",
            description: "Delete a vendor custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCustomFields.delete],
        },
        viewCustomerCertificateCustomField: {
            name: "viewCustomerCertificateCustomField",
            description: "View all certificate custom field",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCertificateCustomFields.read],
        },
        addCustomerCertificateCustomField: {
            name: "addCustomerCertificateCustomField",
            description: "Add a certificates custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCertificateCustomFields.create],
        },
        editCustomerCertificateCustomField: {
            name: "editCustomerCertificateCustomField",
            description: "Edit a certificates custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCertificateCustomFields.update],
        },
        deleteCustomerCertificateCustomField: {
            name: "deleteCustomerCertificateCustomField",
            description: "Delete a certificates custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.customerCertificateCustomFields.delete],
        },
        //To do - use vendor specific featureflags for vendor certificates
        viewVendorCertificateCustomField: {
            name: "viewVendorCertificateCustomField",
            description: "View all certificate custom field",
            subscription: [Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
            ],
            checkAdmin: true,
            permissions: [
                Permissions.settings.vendorCertificateCustomFields.read,
                Permissions.settings.vendorCertificateCustomFields.create,
                Permissions.settings.vendorCertificateCustomFields.update,
                Permissions.settings.vendorCertificateCustomFields.delete,
            ],
            requireAnyPermission: true,
        },
        addVendorCertificateCustomField: {
            name: "addVendorCertificateCustomField",
            description: "Add a certificate custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCertificateCustomFields.create],
        },
        editVendorCertificateCustomField: {
            name: "editVendorCertificateCustomField",
            description: "Edit a certificate custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCertificateCustomFields.update],
        },
        deleteVendorCertificateCustomField: {
            name: "deleteVendorCertificateCustomField",
            description: "Delete a certificates custom field",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            checkAdmin: true,
            permissions: [Permissions.settings.vendorCertificateCustomFields.delete],
        },

        viewLabels: {
            name: "viewLabels",
            description: "View all labels",
            subscription: [
                Subscription.ECMPremium,
                Subscription.ECMPro,
                Subscription.VEPREMIUM,
                Subscription.VEPRO,
            ],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.customerLabel.read],
        },
        addLabel: {
            name: "addLabel",
            description: "Add a label",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.customerLabel.create],
            requireAnyPermission: true,
        },
        editLabel: {
            name: "editLabel",
            description: "Edit a label",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.customerLabel.update],
        },
        deleteLabel: {
            name: "deleteLabel",
            description: "Delete a label",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.customerLabel.delete],
        },
        internalSettings: {
            name: "internalSettings",
            description: "Manage the internal settings",
            subscription: advanceSubscription,
            internalUser: true,
        },
        ecms: {
            name: "ecms",
            description: "Manage ECMS settings",
            internalUser: true,
        },
        retailLocations: {
            name: "retailLocations",
            description: "Manage settings and retail sites",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.index.generalRead],
            checkAdmin: true,
        },
        viewRetailLocations: {
            name: "viewRetailLocations",
            description: "View all retail locations",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.retailSettings.index.read],
            checkAdmin: true,
            setting: "enableRetail",
        },
        addRetailLocations: {
            name: "addRetailLocations",
            description: "Add a retail location",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.retailSettings.index.update],
            checkAdmin: true,
        },
        editRetailLocations: {
            name: "editRetailLocations",
            description: "Edit a retail location",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.retailSettings.location.update],
            checkAdmin: true,
        },
        deleteRetailLocations: {
            name: "deleteRetailLocations",
            description: "Delete a retail location",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.retailSettings.location.delete],
            checkAdmin: true,
        },
        viewProductCategories: {
            name: "viewProductCategories",
            description: "View all Product Categories",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.settings.exemptProductCategories.read],
            checkAdmin: true,
        },
        addProductCategories: {
            name: "addProductCategories",
            description: "Add a product category",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.exemptProductCategories.create],
            checkAdmin: true,
        },
        editProductCategories: {
            name: "editProductCategories",
            description: "Edit a product category",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.exemptProductCategories.update],
            checkAdmin: true,
        },
        deleteProductCategories: {
            name: "deleteProductCategories",
            description: "Delete a product category",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.exemptProductCategories.delete],
            checkAdmin: true,
        },
        useProductCategoriesToggle: {
            name: "useProductCategoriesToggle",
            description: "Use product categories toggle",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.exemptProductCategories.update],
            checkAdmin: true,
        },
        companyGroups: {
            name: "companyGroups",
            description: "Manage Company Groups settings",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            permissions: [Permissions.settings.companyGroups.read],
            checkAdmin: true,
        },
        addEditDeleteCompanyGroups: {
            name: "addEditDeleteCompanyGroups",
            description: "Add, edit, and delete Company Groups",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.MSUser],
            permissions: [Permissions.settings.companyGroups.update],
            checkAdmin: true,
        },
        viewFaxEmails: {
            name: "viewFaxEmails",
            description: "View list of fax emails",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            permissions: [Permissions.settings.index.generalRead],
        },
        addEditDeleteFaxEmails: {
            name: "addEditDeleteFaxEmails",
            description: "Add, edit, and delete fax emails",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.index.generalUpdate],
        },
        viewBucketSettings: {
            name: "viewBucketSettings",
            description: "View bucket settings",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalRead],
            env: [...lowerEnvironments, ...upperEnvironments],
        },
        addEditDeleteBuckets: {
            name: "addEditDeleteBuckets",
            description: "Add, edit, and delete buckets",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.index.generalUpdate],
        },
        addEditDeleteBucketsLocations: {
            name: "addEditDeleteBucketsLocations",
            description: "Add, edit, and delete buckets locations",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.index.generalUpdate],
        },
        viewBucketsLocations: {
            name: "viewBucketsLocations",
            description: "View buckets locations",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.index.generalRead],
            env: [],
        },
        addEditDeleteBucketsUsers: {
            name: "addEditDeleteBucketsUsers",
            description: "Add, edit, and delete buckets users",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.settings.index.generalUpdate],
        },
        userPreferences: {
            name: "userPreferences",
            description: "set user preferences",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.settings.userPreferences.update],
            internalUser: false,
        },
        enableLocalJurisdictionSetting: {
            name: "enableLocalJurisdictionSetting",
            description: "Enable Local Jurisdiction checkbox in settings",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.settings.index.generalUpdate],
            // Open to all eligible users in lower envs; internal users only in upper envs
            internalUserEnv: upperEnvironments,
        },
        enableMultiTaxSetting: {
            name: "enableMultiTaxSetting",
            description: "Enable Multi Tax checkbox in settings",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.settings.index.generalUpdate],
            // Open to all eligible users in lower envs; internal users only in upper envs
            internalUserEnv: upperEnvironments,
        },
        enableAIValidationSetting: {
            name: "enableAIValidationSetting",
            description: "Enable AI validation for auto-saving certificates as valid or invalid",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalRead],
        },
        enableAIScanCoverLetterSetting: {
            name: "enableAIScanCoverLetterSetting",
            description: "Enable AI scan for cover letters",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.settings.index.generalRead],
        },
        enableJobsSetting: {
            name: "enableJobsSetting",
            description: "Setting for enabling jobs feature",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalUpdate],
        },
        enableOverridesSetting: {
            name: "enableOverridesSetting",
            description:
                "Allow users to view and manage exemption overrides on customer exemptions and exempt reasons",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.settings.index.generalUpdate],
        },
    },
    certificateFilters: {
        certificateImageFilter: {
            name: "certificateImageExists",
            description: "Filters certificate based on cerificate image existence",
            subscription: ecmSubscription,
        },
        certificateLabels: {
            name: "certificateLabels",
            description: "certificate labels",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
        },
        reviewedBy: {
            name: "reviewedBy",
            description: "ECM account that reviewed the certificate",
            subscription: [Subscription.ECMPremium, Subscription.ECMPro],
        },
        localJurisdictionsSearchFilter: {
            name: "localJurisdictionsSearchFilter",
            description:
                "Local jurisdictions (cities, counties, districts) filter on certificate search",
            subscription: ["All"],
            setting: "enableLocalJurisdiction",
        },
        priorityFilter: {
            name: "priorityFilter",
            description: "Email Priority",
            subscription: [Subscription.ECMPremium],
        },
        bucketFilter: {
            name: "bucketFilter",
            description: "Bucket",
            subscription: [Subscription.ECMPremium],
            env: [...lowerEnvironments, ...upperEnvironments],
        },
        locationFilter: {
            name: "locationFilter",
            description: "Retail location",
            subscription: [Subscription.ECMPremium],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            setting: "enableRetail",
        },
    },
    requestCertificates: {
        coverLetter: {
            name: "showCoverLetterOptions",
            description: "This allows selection of certificate templates while requesting",
            subscription: [...advanceSubscription, ...vendorSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.coverLetter.index.read],
        },
        createCustomerCoverLetter: {
            name: "createCustomerCoverLetter",
            description: "This allows creation/edit of customer certificate templates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.coverLetter.index.create],
        },
        createVendorCoverLetter: {
            name: "createVendorCoverLetter",
            description: "This allows creation/edit of vendor certificate templates",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.coverLetter.outgoing.create],
        },
        deleteCustomerCoverLetter: {
            name: "deleteCustomerCoverLetter",
            description: "This allows deletion of customer certificate templates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.coverLetter.index.delete],
        },
        deleteVendorCoverLetter: {
            name: "deleteVendorCoverLetter",
            description: "This allows deletion of vendor certificate templates",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.coverLetter.outgoing.delete],
        },
        singleRequest: {
            name: "singleRequest",
            description: "Request Certificate button",
            subscription: [...ecmSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificateRequests.singleRequest.create],
        },
        singleVendorRequest: {
            name: "singleVendorRequest",
            description: "Request Certificate button",
            subscription: [Subscription.VEPREMIUM, Subscription.VEPRO],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificateRequests.singleRequest.create],
            sessionCheck: SessionChecks.VendorInheritance,
        },
        campaign: {
            name: "campaign",
            description: "Bulk request of certificates",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.certificateRequests.index.create],
        },
    },
    fvsUploader: {
        avs: {
            name: "Avs",
            description: "Automatically review certificates ",
            subscription: advanceSubscription,
        },
    },
    vendors: {
        manageVendors: {
            name: "manageVendors",
            description: "Create and edit vendors",
            subscription: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.vendor.index.create, Permissions.vendor.index.update],
            checkAdmin: true,
            sessionCheck: SessionChecks.VendorInheritance,
        },
        bulkImport: {
            name: "bulkImport",
            description: "Bulk import of vendors",
            subscription: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            permissions: [Permissions.vendor.import.create],
            checkAdmin: true,
            sessionCheck: SessionChecks.VendorInheritance,
        },
        saveSearches: {
            name: "saveSearches",
            description: "Save filtered result",
            subscription: vendorSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.savedSearch.index.create],
            checkAdmin: true,
        },
        createAndSendCertificates: {
            name: "CreateAndSendCertificates",
            description: "Create and send certificates",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMCertificateReviewer],
            checkAdmin: true,
            permissions: [Permissions.vendor.certificate.create],
            sessionCheck: SessionChecks.VendorInheritance,
        },
        signaturePad: {
            name: "signaturePad",
            description: "Create signature",
            protected: true,
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMCertificateReviewer],
            permissions: [
                Permissions.accountSignature.index.create,
                Permissions.accountSignature.index.update,
            ],
            checkAdmin: true,
        },
        vendorAdditionalInfo: {
            name: "vendorAdditionalInfo",
            description: "vendor Additional Info",
            subscription: vendorAvaTaxAPSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            permissions: [Permissions.vendor.additionalInfo.read],
        },
        export: {
            name: "manageVendorExport",
            description: "Export search filter result",
            subscription: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.vendor.export.create],
            checkAdmin: true,
        },
        customFields: {
            name: "customFields",
            description: "vendor custom fields",
            subscription: [Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.vendor.customFields.read, Permissions.vendor.index.update],
        },
        customLabels: {
            name: "customLabels",
            description: "vendor custom labels",
            subscription: [Subscription.VEPREMIUM, Subscription.VEPRO],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [
                Permissions.vendor.customLabels.read,
                Permissions.vendor.customLabels.update,
            ],
        },
        showCustomFields: {
            name: "showCustomFields",
            description: "show vendor custom fields",
            subscription: [Subscription.VEPREMIUM],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.MSUser,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.vendor.customFields.read],
        },
        showVendorLabels: {
            name: "showVendorLabels",
            description: "show vendor labels",
            subscription: [Subscription.VEPREMIUM, Subscription.VEPRO],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            permissions: [Permissions.vendor.customLabels.read],
        },
        importCertificateTemplate: {
            name: "importCertificateTemplate",
            description: "show certificate import templates on Import vendors",
            subscription: [
                ...ecmSubscription,
                ...vendorExemptions,
                ...vendorAvaTaxAPSubscription,
                ...avataxTiers,
            ],
            permissions: [Permissions.vendor.importCertificateTemplate.read],
        },
    },
    vendorCertificates: {
        viewVendorCertificates: {
            name: "viewVendorCertificates",
            description: "View vendor certificates",
            subscription: vendorSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            permissions: [Permissions.vendor.certificate.read],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
        },
        saveSearches: {
            name: "saveSearches",
            description: "Save filtered result",
            subscription: vendorSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMPreValidator,
            ],
            permissions: [Permissions.savedSearch.index.create],
            checkAdmin: true,
        },
        manageCertificatePackages: {
            name: "manageCertificatePackages",
            description: "Create and edit certificate packages",
            subscription: vendorPremiumOnlySubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            permissions: [
                Permissions.vendor.packages.create,
                Permissions.vendor.packages.update,
                Permissions.vendor.packages.delete,
                Permissions.vendor.packages.read,
            ],
            checkAdmin: true,
        },
        manageVendorCertificates: {
            name: "manageVendorCertificates",
            description: "Create and edit vendor certificates",
            subscription: [...ecmSubscription, ...vendorPremiumOnlySubscription],
            checkAdmin: true,
            permissions: [
                Permissions.vendor.certificate.create,
                Permissions.vendor.certificate.update,
            ],
            requireAnyPermission: true,
        },
        export: {
            name: "manageVendorCertificateExport",
            description: "Export search filter result",
            subscription: vendorSubscription,
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
        },
    },
    provision: {
        hasECMAccess: {
            name: "hasECMAccess",
            description: "User has ECM access",
            subscription: ecmSubscription,
        },
    },
    overview: {
        dashboard: {
            name: "dashboard",
            description: "Can view overview dashboard",
            subscription: [...advanceSubscription, ...vendorExemptions],
        },
        insights: {
            name: "insights",
            description: "List different tasks that need action",
            subscription: advanceSubscription,
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
        },
        showVendorData: {
            name: "showVendorData",
            description: "Show vendor data on overview page",
            subscription: vendorExemptions,
            permissions: [Permissions.vendor.index.read],
        },
        showCustomerData: {
            name: "showCustomerData",
            description: "Show customer data on overview page",
            subscription: advanceSubscription,
        },
    },
    reports: {
        failedAPIReports: {
            name: "failedAPIReports",
            description: "This tab is used to view reports for failed api calls",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.adminReports.index.read],
        },
        bouncedEmailReport: {
            name: "bouncedEmailReport",
            description: "This tab is used to view the bounced/dropped emails report",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.adminReports.index.read],
            env: [...lowerEnvironments, ...upperEnvironments],
        },
        ecmsSyncReport: {
            name: "ecmsSyncReport",
            description: "This tab is used to view Exemption status (ECM vs AvaTax).",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            permissions: [Permissions.adminReports.index.read],
            env: [...lowerEnvironments, ...upperEnvironments],
            internalUserEnv: upperEnvironments,
        },
        globalAdminReports: {
            name: "globalAdminLogs",
            description: "This tab is used to view global admin logs",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.adminReports.index.read],
        },
        validationReports: {
            name: "validationReports",
            description: "This tab is used to view company validation reports",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.adminReports.index.read],
        },
        globalValidationReports: {
            name: "globalValidationReports",
            description: "This tab is used to view account validation reports",
            subscription: advanceSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.adminReports.index.read],
        },
        viewReports: {
            name: "viewReports",
            description: "List different saved reports",
            subscription: [...advanceSubscription],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.savedSearch.index.read],
        },
        viewVendorReports: {
            name: "viewVendorReports",
            description: "List different saved reports",
            subscription: [...vendorSubscription],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMCertificateReviewer,
                EcmRole.CEMViewer,
            ],
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.savedSearch.index.read],
        },
        filterVendorOnlyReports: {
            name: "filterVendorOnlyReports",
            description:
                "Filter saved searches to show only vendor-related reports for vendor-only subscriptions",
            subscription: [...vendorSubscription],
        },
        viewEcmReports: {
            name: "viewEcmReports",
            description: "View ECM report types (Certificate, Customer, Pending-Certificate)",
            subscription: [...ecmSubscription],
        },
    },
    afcContent: {
        associateTaxTypeData: {
            name: "associateTaxTypeData",
            description: "Associate tax type data for telecom features",
            subscription: [Subscription.ECMProComms, Subscription.ECMPremiumComms],
        },
        telecomFeatures: {
            name: "telecomFeatures",
            description: "Access to telecom-specific features and content",
            subscription: [Subscription.ECMProComms, Subscription.ECMPremiumComms],
        },
    },
    retail: {
        index: {
            name: "retail",
            description: "Retail landing section containing search form",
            subscription: [Subscription.ECMPremium],
            ecmRole: [...ecmRETRole, EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.read],
        },
        search: {
            name: "search",
            description: "Search customer and its certificatess",
            subscription: [Subscription.ECMPremium],
            ecmRole: [...ecmRETRole, EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.read],
        },
        viewCustomerAndCertificate: {
            name: "viewCustomerAndCertificate",
            description: "View customer and its certificatess",
            subscription: [Subscription.ECMPremium],
            ecmRole: [...ecmRETRole, EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.read],
        },
        editCustomerAndCertificate: {
            name: "editCustomerAndCertificate",
            description: "Manage customer and its certificatess",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.RETManager,
                EcmRole.RETAssociate,
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.update],
        },
        editCustomer: {
            name: "editCustomer",
            description: "Manage customer information",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.RETManager,
                EcmRole.RETAssociate,
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.update],
        },
        viewCustomer: {
            name: "viewCustomer",
            description: "View customer information",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.RETManager,
                EcmRole.RETAssociate,
                EcmRole.RETViewer,
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.read],
        },
        tile: {
            name: "tile",
            description: "View retail tile in overview screen",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retail.index.read],
            setting: "enableRetail",
        },
        selectLocation: {
            name: "selectLocation",
            description: "View the location selector",
            subscription: [Subscription.ECMPremium],
            ecmRole: [
                EcmRole.RETManager,
                EcmRole.RETAssociate,
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
            ],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
        },
        enableRetailSetting: {
            name: "enableRetailSetting",
            description: "Enable retail checkbox in settings",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            avataxRole: [AvataxRole.ECMAccountUser, AvataxRole.ECMCompanyUser],
            permissions: [Permissions.retailSettings.index.read],
        },
    },
    userRoleManagement: {
        edit: {
            name: "edit",
            description: "Edit user role management",
            subscription: userRoleAdminSubscription,
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.userRoleManagement.manage],
        },
        assignRetailLocation: {
            name: "assignRetailLocation",
            description: "Assign retail locations",
            subscription: [Subscription.ECMPremium],
            ecmRole: [EcmRole.CEMAdmin],
            checkAdmin: true,
            permissions: [Permissions.userRoleManagement.manage],
        },
    },
    avatax: {
        entityUseCode: {
            name: "entityUseCode",
            description: "Entity use code for AvaTax",
            subscription: [...avaTaxSubscription],
        },
        categories: {
            name: "avataxCategories",
            description: "Fetch and display AvaTax product categories",
            subscription: [...avaTaxSubscription],
        },
    },
    aiSearch: {
        smartSearch: {
            name: "smartSearch",
            description: "AI-powered smart search functionality",
            subscription: [
                Subscription.ECMPro,
                Subscription.ECMPremium,
                Subscription.VEPRO,
                Subscription.VEPREMIUM,
            ],
        },
    },
    jobs: {
        index: {
            name: "jobs",
            description: "Jobs search page",
            subscription: advanceSubscription,
            setting: "usesJobs",
        },
        create: {
            name: "createJob",
            description: "Create a new job",
            subscription: [...lowerSubscription, ...ecmSubscription],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
            checkAdmin: true,
            permissions: [Permissions.customer.write],
            setting: "usesJobs",
        },
        tile: {
            name: "jobsTile",
            description: "Jobs tile on overview page",
            subscription: advanceSubscription,
            setting: "usesJobs",
        },
    },
    certificateAttachmentLabels: {
        dropdown: {
            name: "certificateAttachmentLabelsDropdown",
            description: "Document label dropdown for supplemental attachments",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            permissions: [Permissions.certificate.read],
        },
        manage: {
            name: "certificateAttachmentLabelsManage",
            description: "Manage certificate attachment labels in settings",
            subscription: [Subscription.ECMPremium, Subscription.VEPREMIUM],
            ecmRole: [EcmRole.CEMAdmin, EcmRole.CEMManager],
            permissions: [Permissions.settings.certificateLabel.read],
        },
    },
    imports: {
        globalImports: {
            name: "globalImports",
            description:
                "Use AvaApp Global Imports (/imports with returnUrl) instead of legacy ECM import pages",
            subscription: ["All"],
            ecmRole: [
                EcmRole.CEMAdmin,
                EcmRole.CEMManager,
                EcmRole.CEMPreValidator,
                EcmRole.CEMCertificateReviewer,
            ],
            checkAdmin: true,
            permissions: [
                Permissions.customer.bulk,
                Permissions.certificate.bulkUpload.create,
                Permissions.vendor.import.create,
            ],
            requireAnyPermission: true,
        },
    },
    managedService: {
        manageAccounts: {
            name: "manageAccounts",
            description: "Import accounts and toggle per-account MS automation (Accounts tab)",
            subscription: ["All"],
            checkAdmin: true,
            permissions: [Permissions.managedService.accounts.write],
        },
        sendToAi: {
            name: "sendToAi",
            description: "Send an ECM certificate to AI for validation (Certificates tab)",
            subscription: ["All"],
            checkAdmin: true,
            permissions: [Permissions.managedService.certificates.sendToAi],
        },
        manageForms: {
            name: "manageForms",
            description: "Toggle a form template's process/populate/complete flags (Forms tab)",
            subscription: ["All"],
            checkAdmin: true,
            permissions: [Permissions.managedService.forms.write],
        },
        manageRules: {
            name: "manageRules",
            description:
                "Author validation rules — edit a pack as JSON, edit a single rule, roll a pack back, onboard a form or company (Rules tab). Read stays open to anyone who can reach the tab; checkAdmin keeps a Technical Support User out of the writes.",
            subscription: ["All"],
            checkAdmin: true,
            permissions: [Permissions.managedService.rules.write],
        },
    },
};

export default featureFlag;
